import { createSlice } from "@reduxjs/toolkit";
import { selectFilterSelections } from "./filters.js";
import { selectDisplayedPhotoForAnnotations } from "./interface.js";

// api client
import apiClient from "./apiClient.js";
import { toGraphQLQuery } from "./graphQLHelpers.js";

// Reselect (Memoized Selectors)
import { createSelector } from 'reselect';

// Ivon-e utils
import { cleanCorruptedAnnotation } from "./ivone_utils.js";

function onlyUnique(value, index, self) {
  return self.indexOf(value) === index;
}

// Returns the keys that always exists and that are searchable
// In practice, it's the "createdById" field, for users that have created annotations
const ALWAYS_EXISTING_KEYS = (state) => {
  // get users who annotated
  let usersIds = state.annotations.elements.reduce((acc, ann) => {
    acc[ann.createdById] = true;
    return acc
  }, {})
  usersIds = Object.keys(usersIds);

  // return
  return [
    {
      name: "createdById",
      title: "Author",
      type: "select",
      options: state.users.all
        .filter((u) => {
          return (
            usersIds.indexOf(u.id) > -1
          );
        })
        .map((u) => {
          return { id: u.id, text: u.name };
        }),
    },
  ];
};

// all keys - no filtering
export const allDescriptionKeys = (state) => {
  return state.annotations.description.keys;
}

export const selectViewSpec = (state) => {
  return state.annotations.description
}

// all keys without the submenus
const keys_not_submenus = (state) => {
  return [
    ...state.annotations.description.keys.filter((k) => !Object.keys(submenus(state)).includes(k.id)),
    ...ALWAYS_EXISTING_KEYS(state),
  ];  
}

// all keys including submenus
const keys_with_submenus = (state) => {
  return [
    ...state.annotations.description.keys,
    ...ALWAYS_EXISTING_KEYS(state),
  ];
}

// list the submenus in an object where : 
// - key : submenu id
// - value : the submenu "key" object 
const submenus = (state) => {
  let submenus_ids = []
  const keyPerId = {}
  for(let ik=0; ik<state.annotations.description.keys.length; ik++){
    const k = state.annotations.description.keys[ik]
    if (k.id !== undefined){
      keyPerId[k.id] = k
    }
    if(k.type === "select"){
      for(let io=0; io < k.options.length;io++){
        const o = k.options[io]
        if (o.submenus !== undefined){
          for (let is=0; is < o.submenus.length; is++){
            submenus_ids.push(o.submenus[is])
          }
        }
      }
    }
  }

  // keep only the submenus
  return Object.keys(keyPerId)
    .filter((key) => submenus_ids.includes(key))
    .reduce((obj,key) => {
      obj[key] = keyPerId[key];
      return obj
    },{})
}




const REQUESTS = {
  delete: ({ ids, inspectionId }) => {
    return `mutation {
        deleteAnnotations(ids: ${JSON.stringify(ids)}, inspectionId: "${inspectionId}")
     }`;
  },
  index: ({ viewId, inspectionId }) => {
    return `query {
      view(id:"${viewId}"){
        id,
        spec
      },
      annotations(viewId: "${viewId}", inspectionId: "${inspectionId}"){
        id,
        viewId,
        inspectionId,
        photoId,
        createdById,
        createdByEmail,
        geometry {
          type,
          coordinates
        },
        geometryName,
        data,
        text {
          value,
          color
        },
        position
      }
    }`;
  },
  editView: ({ viewId, spec }) => {
    // Convert spec to GraphQL object format using toGraphQLQuery
    // Note: submenus are included in options and must be accepted by backend ViewSpecKeyOptionsInput
    return `mutation {
      editView(view: {
        id: "${viewId}",
        spec: ${toGraphQLQuery(spec)}
      }) {
        id,
        spec
      }
    }`;
  },
  availableViews: () => {
    return `query {
      availableViews {
        id,
        project {
          id,
          name
        },
        inspection {
          id,
          name
        }
      }
    }`;
  },
  listSymbologyFiles: (type) => {
    return `query {
      listSymbologyFiles(type: "${type}") {
        files
      }
    }`;
  },
  create: ({
    viewId,
    inspectionId,
    geometryName,
    data,
    geometry,
    text,
    photoId = undefined,
  }) => {
    return `mutation {
      createAnnotation(annotation: {
        viewId: "${viewId}",
        inspectionId: "${inspectionId}",
        ${photoId != null ? `photoId: "${photoId}",` : ''}
        geometryName: "${geometryName}",
        data: ${JSON.stringify(JSON.stringify(data ?? {}))},
        ${text ? `text: { value: ${JSON.stringify(text.value)}, color: ${JSON.stringify(text.color)} },` : ''}
        geometry: {
          type: "${geometry.type}",
          coordinates: ${JSON.stringify(JSON.stringify(geometry.coordinates))}
        }
      }){
        id,
        viewId,
        inspectionId,
        photoId,
        createdById,
        geometry {
          type,
          coordinates
        }
        geometryName,
        data,
        text {
          value,
          color
        },
        position
      }
    }`;
  },
  updateMany: ({ids, geometry, geometryName, data, text, inspectionId}) => {
    const query = {
      ids,
      ...(geometry && { geometry }),
      ...(geometryName && { geometryName }),
      ...(data && { data }),
      ...(text && { text }),
      inspectionId
    };
    return `mutation {
      editAnnotations(annotations: ${toGraphQLQuery(query)})
      {
        id,
        inspectionId,
        createdById,
        geometry {
          type,
          coordinates
        }
        geometryName,
        data,
        text {
          value,
          color
        },
        position
      }
    }`
  }
};

let initialState = {
  description: {
    keys: [],
  },
  sidebarConfig: {},
  // description: {
  //   keys: [
  //     {
  //       name: "category",
  //       title: "Category",
  //       type: "select",
  //       options: [
  //         {id: "c0", text: "Sample Category 1"},
  //         {id: "c1", text:"Sample Category 2"}
  //       ],
  //       default: "c1"
  //     },
  //     {
  //       name: "level",
  //       title: "Level",
  //       type: "number",
  //       min: 0,
  //       max: 5,
  //       default: 0
  //     },
  //     {
  //       name: "title",
  //       title: "Title",
  //       type: "text",
  //       default: ""
  //     },
  //     {
  //       name: "message",
  //       title: "Message",
  //       type: "textarea",
  //       default: ""
  //     },
  //     {
  //       name: "idFixed",
  //       title: "Fixed?",
  //       type: "checkbox",
  //       default: false
  //     },
  //     {
  //       name: "inspectionDate",
  //       title: "Inspection Date",
  //       type: "date",
  //       default: undefined
  //     },
  //     {
  //       name: "snackTime",
  //       title: "Snack Date",
  //       type: "datetime-local",
  //       default: undefined
  //     },
  //     {
  //       name: "email",
  //       title: "Email",
  //       type: "email",
  //       default: ""
  //     }
  //   ]
  // },
  // sidebarConfig: {
  //   levelKey: "level",
  //   categoryKey: "category",
  //   titleKey: "title"
  // },
  elements: [
    // {
    //   geometry: {
    //     type:"Point",
    //     coordinates: [8000,57600-1000]
    //   },
    //   geometryName: "point",
    //   category: "0",
    //   level: 3,
    //   message: "This is the message"
    // },
    // {
    //   geometry: {
    //     type:"Point",
    //     coordinates: [9000,57600-1000]
    //   },
    //   geometryName: "square",
    //   category: "1",
    //   level: 3,
    //   message: "This is the message"
    // },
    // {
    //   geometry: {
    //     type:"Polygon",
    //     coordinates: [
    //       [10000+500,57600-1000+500],
    //       [10000-500,57600-1000+500],
    //       [10000-500,57600-1000-500],
    //       [10000+500,57600-1000-500],
    //       [10000+500,57600-1000+500]
    //     ]
    //   },
    //   geometryName: "polygon",
    //   category: "Sample Category 2",
    //   level: 3,
    //   message: "This is the message 2"
    // }
  ],
  candidateAnnotations: [],
  annotationState: "SelectionOnGoing",
  presets: [],
  // List of styles possibly applied on annotations depending on context in mapWrapper
  // annotationStyles: {
  //    viewStyleChart: {} styles that depend on the annotation field values (=symbology), defined in the View
  //    appStyleChart: {} app default style, passed to features and replaced by view style if needed
  // }
  annotationStyles: {
    // app default style settings
    appStyleChart: {
      feature: {
        fill: {color: [0, 0, 0, 0]},
        stroke: {color: [255, 165, 100, 1], width: 2}
      },
      candidateAnnotation: {
        fill: {color: [255, 204, 51, 0.5]},
        stroke: {color: [255, 204, 51, 1], width: 2, lineDash: [10, 5]}
      },
      hover: {
        fill: {color: [255, 204, 51, 0.5]},
        stroke: {color: [255, 204, 51, 1], width: 2}
      },
      gradient: {
        min: [255,0,0],
        max: [0,255,0]
      },
      selected: {
        halo: { color: [255, 255, 255, 0.95], width: 8 },
        stroke: { width: 3 },
      }
    },
    viewStyleChart: {},
    // label displayed on features, contains keys name as a value
    label: "None",
  }
};

// configuration elements
const filterableTypes = ["select"]; // 2025-02-13 pas fonctionnel, désactivé pour le moment: ["number", "checkbox"]

// // create fake annotations - DEVDEVDEV
// const nbAnnotationsToCreate = 100
// const coordDomain = [18944,57600]
// const createNewAnnotation = ({geometryName}) => {
//   const pt = [parseInt(Math.random()*coordDomain[0]),parseInt(Math.random()*coordDomain[1])]
//   let output = {
//     geometry: {
//       type:"Point",
//       coordinates: pt
//     },
//     geometryName: geometryName,
//     title: Math.random().toString(36).replace(/[^a-z]+/g, '').substr(0, 5),
//     id: Math.random().toString(36).replace(/[^a-z]+/g, '').substr(0, 5)
//   }
//   for(let i = 0; i< initialState.description.keys.length; i++){
//     const k = initialState.description.keys[i]
//     switch(k.type){
//       case "select":
//         output[k.name] = k.options[Math.floor(Math.random() * k.options.length)].id;
//         break;
//       case "number":
//         output[k.name] = k.min + Math.floor(Math.random() * (k.max - k.min + 0.5));
//         break;
//       case "checkbox":
//         output[k.name] = Math.random() > 0.5;
//         break;
//       case "date":
//       case "datetime-local":
//         const startDate = (new Date("2010-01-01"))
//         const endDate = (new Date("2022-01-01"))
//         output[k.name] = (new Date((startDate.getTime() + Math.random() * (endDate.getTime() - startDate.getTime())))).toISOString()
//         break;
//     }
//   }
//   return output
// }

// for(let i = 0; i<nbAnnotationsToCreate; i++){
//   const r = Math.random() * 2;
//   if(r < 1){
//     // we create a point
//     initialState.elements = [
//       ...initialState.elements,
//       createNewAnnotation({geometryName: "point"})
//     ]
//   }else if(r < 2){
//     // // we create a square
//     initialState.elements = [
//       ...initialState.elements,
//       createNewAnnotation({geometryName: "square"})
//     ]
//   }
// }

// // /DEVDEVDEV

// // DEVDDEVDEV id creation
// const createId = () => {
//   return Math.random().toString(36).replace(/[^a-z]+/g, '').substr(0, 5)
// }





export const annotationsApi = {
  selectCreate: (state) => {
    return async (payload, next) => {
      // Clean annotation:
      if (state.annotations.candidateAnnotations[0].data == null) next(null); // Error, unexpected
      const cleaning_results = cleanCorruptedAnnotation(state.annotations.description.keys, state.annotations.candidateAnnotations[0], "remove");

      // If corrupted, refuse creation
      if (cleaning_results.corrupted_field_names.length > 0){
        console.log("It was about to create a corrutped annotation!")
        console.log(JSON.stringify(state.annotations.candidateAnnotations[0]))
        console.log(cleaning_results.corrupted_field_names)
        next(null); // This is the current way of telling the caller that there has been an error...
      }

      const cand = {
        data: cleaning_results.data,
        ...payload
      }

      // Make main request
      const result = await apiClient.req(
        REQUESTS.create({
          viewId: cand.viewId,
          inspectionId: cand.inspectionId,
          geometryName: cand.geometryName,
          geometry: cand.geometry,
          data: cand.data,
          photoId: payload.photoId,
        })
      );
      // Add user event
      await apiClient.create_user_event({
        userId: cand.createdById,
        category: "annotation#create",
        other_infos: {
          id: result.data.data.createAnnotation.id,
          inspectionId: cand.inspectionId
        }
      });
      //
      next(result.data.data.createAnnotation);
    };
  },
  index: async ({ viewId, inspectionId }, next) => {
    const result = await apiClient.req(
      REQUESTS.index({ viewId, inspectionId })
    );
    next(result.data.data);
  },
  checkAnnotationsExist: async ({ viewId, inspectionId }, next) => {
    // Check if annotations exist for this view/inspection (lightweight query)
    const result = await apiClient.req(
      REQUESTS.index({ viewId, inspectionId })
    );
    const annotations = result.data.data?.annotations || [];
    const hasAnnotations = annotations.length > 0;
    if (next) next(hasAnnotations);
    return hasAnnotations;
  },
  editView: async ({ viewId, spec }, next) => {
    const result = await apiClient.req(
      REQUESTS.editView({ viewId, spec })
    );
    if (result.data.errors != null) {
      const errorMessages = result.data.errors.map(e => e.message || JSON.stringify(e)).join('\n');
      throw new Error(errorMessages);
    }
    if (next) next(result.data.data.editView);
    return result.data.data.editView;
  },
  availableViews: async (next) => {
    const result = await apiClient.req(
      REQUESTS.availableViews()
    );
    if (next) next(result.data.data.availableViews);
    return result.data.data.availableViews;
  },
  listSymbologyFiles: async (type, next) => {
    const result = await apiClient.req(
      REQUESTS.listSymbologyFiles(type)
    );
    const files = result.data.data?.listSymbologyFiles?.files || [];
    if (next) next(files);
    return files;
  },
  delete: async ({ ids, inspectionId, userId}, next) => {
    // Make main request
    const result = await apiClient.req(REQUESTS.delete({ids, inspectionId}));
    // Add user event
    await apiClient.create_user_event({
      userId: userId,
      category: "annotation#delete",
      other_infos: {
        ids: ids,
        inspectionId: inspectionId
      }
    });
    next(result.data.data.deleteAnnotations);
  },
  // update a list of annotations
  update: (state) => {
    return async (payload, next) => {
      // Make main request
      const result = await apiClient.req(
        REQUESTS.updateMany({ 
          ...payload
        })
      );
      // Add user event
      const eventData = {
        userId: state.auth.user_id,
        category: "annotation#update",
        other_infos: {
          ids: payload.ids,
          inspectionId: payload.inspectionId,
          fields: [
            ...(payload.data ? Object.keys(payload.data) : []),
            ...(payload.geometry ? ["geometry", "geometryName"] : [])
          ]
        }
      }
      await apiClient.create_user_event(eventData);
      next(result.data.data.editAnnotations);
    };
  }
};

/*
  - a createSlice create an object wich is is a part of the reduc state,
    it contains a name an initial state, actions and reducers
  - the functions listed below are reducers, it's used to update elements of the state.
  - it must be used in a dispatch()
  
  Annotation states:
  - elements: existing annotations
  - candidateAnnotations: Array // the inputs of the form in the annotation tab. ALWAYS AN ARRAY OF 1 ELEMENT ONLY
    possible values:
    - An object with the form inputs
    - empty array []
  - annotationState: string // step in wich the annotate panel is currently in
    possible values and meaning:
      - DefaultForm // state in which the user didn't modify any values in the form
      - FormFilling // state in which the user filled the form (even partially) but didn't start drawing
      - DrawOnGoing // state in which the user is currently drawing (trigger on drawstart => first clic on map)
      - AwaitingCreateValidation // state in which the user ended the drawing (trigger on drawend => double clic on last point) and annotation was send to the API awaiting an response
      - AnnotationCreated // state in which the backend responded and annotation is complete
      - AnnotationRejected // state in which the backend responded but annotation creation went wrongly
      - AnnotationRedrawOnGoing // Redraw button was hit selected annotation geometry was hidden and draw toggled
      - AnnotationModifyOnGoing // Edit annotation geometry using openlayer snapping (https://openlayers.org/en/latest/examples/snap.html)
      - AnnotationPostVertexEditOnGoing // Edit annotation geometry using draw interactionn allowing us to redraw a part of the geometry only
      - AnnotationPreVertexEditOnGoing // Edit annotation geometry using draw interactionn allowing us to redraw a part of the geometry only
      - AnnotationEdited // state in which the backend responded and annotation is successfully updated
      - AnnotationEditionRejected // state in which the backend responded and annotation update went wrongly
      - SelectionOnGoing // Selection has been toggled in annotate panel by pressing S
  
  Note that in this state we access to data with <state.<key>>
  in the global state you access them with <state.annotations.<key>>
  the <annotations> is defined by the name of the object added with createSlice
*/
export const annotationsSlice = createSlice({
  name: "annotations",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    deleteAnnotations: (state, { payload: { ids } }) => {
      return {
        ...state,
        elements: state.elements.filter((el) => !ids.includes(el.id)),
      };
    },
    // Update a single annotation in the state (keep list order)
    updateAnnotation: (state, { payload }) => {
      const annotation = { ...payload };
      const index = state.elements.findIndex((el) => el.id === annotation.id);
      if (index < 0) {
        return { ...state, elements: [...state.elements, annotation] };
      }
      const elements = [...state.elements];
      elements[index] = annotation;
      return { ...state, elements };
    },
    // Update a list of annotation in the state
    updateAnnotations: (state, { payload }) => {
      const annotations = [
        ...payload,
      ];
      return {
        ...state,
        elements: [...annotations, ...state.elements.filter(el => !annotations.map(a => a.id).includes(el.id))],
      };
    },
    addCandidateAnnotation: (state, { payload }) => {
      // will have to save to db
      return {
        ...state,
        candidateAnnotations: [payload],
      };
    },
    clearCandidateAnnotations: (state) => {
      return {
        ...state,
        candidateAnnotations: [],
      }
    },
    replaceAnnotations: (state, { payload }) => {
      return {
        ...state,
        elements: payload,
      };
    },
    updateViewSpec: (state, { payload: { spec } }) => {
      if (spec.description && spec.description.keys) {
        // ading a uid to keys for those without it
        spec.description.keys = spec.description.keys.map((k) => {
          // rules are:
          // - there always is a name
          // - in case thare are identical names add id
          let id = k.id ? k.id+"-" : ""
          let name = k.name
          return {
            ...k,
            uid: k.uid ? k.uid : `${id}${name}`,
          }
        })
      }
      return {
        ...state,
        ...spec,
      };
    },
    createAnnotation: (state, { payload }) => {
      const cand = {
        ...state.candidateAnnotations[0],
        ...payload,
      };
      return {
        ...state,
        elements: [...state.elements, cand],
      };
    },
    updateAnnotationState: (state, {payload}) => {
      return {
        ...state,
        annotationState: payload,
      }
    },
    setPresets: (state, {payload}) => {
      return {
        ...state,
        presets: payload,
      }
    },
    setViewAnnotationStyles: (state, {payload}) => {
      return {
        ...state,
        annotationStyles: {
          ...state.annotationStyles,
          viewStyleChart: payload,
        }
      }
    },
    updateAnnotationStyleLabel: (state, {payload}) => {
      return {
        ...state,
        annotationStyles: {
          ...state.annotationStyles,
          label: payload
        }
      }
    }
  },
});

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
export const selectAnnotations = (state) => state.annotations.elements

export const selectAnnotationState = (state) => {
  return state.annotations.annotationState;
}

export const selectPresets = (state) => {
  return state.annotations.presets;
}

export const selectViewStyleChart = (state) => {
  return state.annotations.annotationStyles.viewStyleChart
}

export const selectPositionReferences = (state) => {
  let output = [];
  for (const a of state.annotations.elements) {
    for (const k of Object.keys(a.position)) {
      if (k !== "length" || k !== "area") {
        if (output.indexOf(k) < 0) {
          output.push(k);
        }
      }
    }
  }
  return output;
};







// get list of annotations data object fields
const getAnnotationsDataFieldTitles = (state) => {
  const description = state.annotations.description;
  const subs = submenus(state);

  const isSubmenu = (key) => Object.keys(subs).includes(key.id);

  // Démarre avec les clés qui ne sont pas des sousmenus
  let keys = description.keys.filter(k => !isSubmenu(k));
  let output = [...keys];

  let currentLength = output.length;
  for (let i = 0; i < currentLength; i++) {
    const key = output[i];

    if (key.type === "select" && Array.isArray(key.options)) {
      for (const opt of key.options) {
        if (opt.submenus) {
          for (const submenuId of opt.submenus) {
            const submenuKey = subs[submenuId];
            if (
              submenuKey &&
              !output.some(existing => existing.id === submenuKey.id)
            ) {
              output.push(submenuKey);
              currentLength++; // continuer la boucle
            }
          }
        }
      }
    }
  }

  // On retourne la liste des objets `{ name, title }` uniques par `name`
  const seen = new Set();
  return output
    .filter(k => k.name && k.title && !seen.has(k.name) && seen.add(k.name))
    .map(k => ({ name: k.name, title: k.title }));
};

// function to dynamically build the annotation field for the current 
// annotation
const annotationFieldsCreator = (state) => {
  return (annotationData) => {
    // management of submenus
    const description = state.annotations.description
    // get all the submenus
    const subs = submenus(state)
    
    // init with only the menus that are not submenus
    let output = {
      keys: description.keys.filter((k) => Object.keys(subs).indexOf(k.id) < 0)
    }
    // add the submenus
    let currOutputLength = output.keys.length
    for (let ik=0; ik<currOutputLength;ik++){
      const k = output.keys[ik]
      if (k.type === "select"){
        let o = k.options.filter((o) => o.id === annotationData[k.name])[0]

        // If no value, let's choose the default value
        if (o === undefined){
          o = k.options.filter((o) => o.id === k.default)[0];
        }
        // If it has submenus, add them
        if (o.submenus !== undefined){
          for (let is=0; is< o.submenus.length; is++){
            output.keys.push(subs[o.submenus[is]])
            currOutputLength+=1
          }
        }
      }
    }

    //  sort output.keys depending on index 
    output.keys.sort((a, b) => {
      return description.keys.findIndex(key => key === a) - description.keys.findIndex(key => key === b);
    });
    return output;
  };
};


// select the fields for the forms dynamically (depending of the annotation)
export const selectDynamicAnnotationFields = (state) => {
  return annotationFieldsCreator(state)
}

export const SelectAnnotationsDataFields = (state) => {
  return getAnnotationsDataFieldTitles(state)
}

// all the fields for each key (merging all submenus)
// in this output there is no more submenus, only 
// every key (correpsonding to every "name") while merging 
// all the values of different submenus with the same "name"
export const selectAnnotationFields = createSelector( [selectViewSpec], (description) => {
  // let's regroup the fields similar of the submenus
  const output = {
    ...description,
    keys: []
  }
  const fields = Object.groupBy(description.keys,(k) => k.name)
  const fields_keys = Object.keys(fields)
  for(let iif=0;iif<fields_keys.length;iif++){
    const k = fields_keys[iif]
    const keysToGroup = fields[k]
    let thisKey = {}
    switch(keysToGroup[0].type){
      case "select":
        thisKey = {
          "name":keysToGroup[0].name,
          "title":keysToGroup[0].title,
          "type":"select",
          "options":[],
          "default": keysToGroup[0].default // We just take the 1st default value that we find...
        }
        for(let ik=0; ik<keysToGroup.length; ik++){
          thisKey.options = [
            ...thisKey.options,
            ...keysToGroup[ik].options
          ]
        }
      break;
      default:
        // in this key we present only one key
        thisKey = {...keysToGroup[0]}
    }
    output.keys.push(thisKey)
  }

  return output
});


export const selectCandidateAnnotations = (state) =>
  state.annotations.candidateAnnotations;
export const selectSidebarConfig = (state) => state.annotations.sidebarConfig;











// create a data object containning a field per key in the view using default value for each
//  used to fill data field in eahc annotations
export const buildDefaultAnnotationData = (keys) => {
  const data = {};

  const getKeyById = (id) => keys.find(k => k.id === id);

  const fillField = (field, data) => {
    // prevent redefined of a value
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Nullish_coalescing
    data[field.name] = data[field.name] ?? field.default;

    if (field.type === "date" || field.type === "datetime-local") {
      return; // TODO: gérer la date
    }

    // handles select type keys
    if (field.type === "select") {
      // get options from the key
      const option = field.options?.find(o => o.id === data[field.name]);
      // check if thekey has submenues
      if (option?.submenus?.length) {
        // if it does handles the field impacted by the submenu to ensure the menu/submenu relation in the fields
        option.submenus.forEach(subId => {
          const subField = getKeyById(subId);
          if (subField) {
            // using recursive to handle any depth in the submenues logic
            fillField(subField, data);
          }
        });
      }
    }
  };

  keys.forEach(k => fillField(k, data));
  return data;
};

// return an annotation filled with default values for each field based on menu and submenues
export const emptyAnnotation = (state) => {
  // get keys from the view
  const keys = state.annotations.description.keys;

  return {
    data: buildDefaultAnnotationData(keys),
  };
};


// DEPRECATED
export const selectColorableKeys = (state) => {
  return keys_not_submenus(state).filter(
    (el) => ["select", "number", "checkbox"].indexOf(el.type) > -1
  );
};

// List the select options associated with a symbology
export const selectOptionsWithSymbology = (state) => {
  // Filter all keys to get only the select ones
  const filteredKeys = state.annotations.description.keys.filter(key => key.type === "select");
  const selectsOptions = [];

  // get options + add key uid field to it
  for (let fk of filteredKeys) {
    selectsOptions.push(
      ...fk.options.map(o => ({ ...o, keyName: fk.name, keyUid: fk.uid, title: fk.title }))
    );
  }

  const allOptionsWithSymbology = selectsOptions
  .filter(option => option.hasOwnProperty("color") || option.hasOwnProperty("symbol") || option.hasOwnProperty("fill_pattern"))
  .map(option => {
    const base = {
      title: option.title,
      keyName: option.keyName,
      keyUid: option.keyUid,
      id: option.id,
      text: option.text,
      level: state.annotations.description.keys.findIndex((k) => k.uid === option.keyUid),
      ...(option.hasOwnProperty("color") ? { color: option.color } : {}),
      ...(option.hasOwnProperty("symbol") ? { symbol: option.symbol } : {}),
      ...(option.hasOwnProperty("fill_pattern") ? { fill_pattern: option.fill_pattern } : {}),
    };
    return base;
  });

  return allOptionsWithSymbology;
};

// Compute the symbology corresponding to the annotation
// Inputs:
// - ann: the annotation
// - coloredOptionsFromKeys: list returned by the selectOptionsWithSymbology() function
// Returns the symbology element (with a "color" key [r, g, b]), or null if no symbology can be found
export const getAnnotationSymbology = (ann, coloredOptionsFromKeys) => {
  // Find in the symbology the list of colors that apply to the annotation
  const colors = []
  for (const name in ann.data) {
    // We look for the options with symbology that has the correct name of field and the correct value (option id)
    const color = coloredOptionsFromKeys.filter(k => k.keyName === name && k.id === ann.data[name]);
    if (color.length > 0)
      colors.push(color[0]) // the array length must be 1 as there can't be more than 1 option for this field name with the same ID
  }
  // If no symbology found, return null
  if (colors.length === 0) return null;


  const getMaxLevelOptionForField = (field) => {
    return colors
      .filter(option => option[field] !== undefined)
      .reduce((maxOpt, current) => current.level > maxOpt.level ? current : maxOpt, { level: -Infinity });
  };

  // For each field (color, symbol, fill_pattern), find the option with the highest level that has it defined
  const colorOption = getMaxLevelOptionForField("color");
  const symbolOption = getMaxLevelOptionForField("symbol");
  const fillPatternOption = getMaxLevelOptionForField("fill_pattern");

  // Build the symbology object including the keyName from the colorOption or fallback to first option
  const symbology = {
    id : (colorOption.level !== -Infinity) ? colorOption.id : colors[0].id,
    keyName: (colorOption.level !== -Infinity) ? colorOption.keyName : colors[0].keyName,
    ...(colorOption.level !== -Infinity ? { color: colorOption.color } : {}),
    ...(symbolOption.level !== -Infinity ? { symbol: symbolOption.symbol } : {}),
    ...(fillPatternOption.level !== -Infinity ? { fill_pattern: fillPatternOption.fill_pattern } : {}),
  };

  return symbology;
};

// Returns the complete list of fields from the view
// used to build the filters in the sidebar and the search input
export const selectFilterableFields = (state) => {
  return keys_with_submenus(state).filter((el) => {
    if (el.name === "createdById")
      return false // no filter on author
    return filterableTypes.indexOf(el.type) > -1
  });
};

// export const selectSearchableFields = (state) => {
//   return state.annotations.description.keys.keys.filter((el) => (["text","textarea"].indexOf(el.type) > -1))
// }
export const selectGetColorRatio = (state) => {
  return (annotation) => {
    const colorKey = keys_not_submenus(state).filter(
      (el) => el.name === state.filters.colorKey
    )[0];
    if (colorKey === undefined) {
      return undefined;
    }
    switch (colorKey.type) {
      case "select":
        return (
          colorKey.options
            .map((el) => el.id)
            .indexOf(annotation.data[colorKey.name]) /
          (colorKey.options.length - 1)
        );
      case "number":
        return (
          (annotation.data[colorKey.name] - colorKey.min) /
          (colorKey.max - colorKey.min)
        );
      case "checkbox":
        if (annotation.data[colorKey.name]) {
          return 1;
        } else {
          return 0;
        }
      default:
        return undefined;
    }
  };
};





// inputs:
//  - k is in keys list from function selectAnnotationFields => every submenus for the same name are merged in it
//  - ann: annotation
// Get the value to display (string)
export const get_display_value = (k, ann, keys) => {
  return cleanCorruptedAnnotation(keys, ann, "display").data[k.name];
}

export default annotationsSlice.reducer;

// take a data object (from an annotation) and update one field, resetting
// submenu fields when a parent select value changes
export function updateOneFieldOfDataFromKeys(descriptionKeys, oldData, key, value) {
  let updatedData = { ...oldData, [key]: value };
  let keysChanged = [key];
  const thisKey = descriptionKeys.filter((k) => {
    if (k.name === key) {
      if (k.type === 'select') {
        return k.options.map((o) => o.id).includes(value);
      }
      return true; // submenus only exist on select
    }
    return false;
  })[0];
  if (thisKey?.type === 'select') {
    const thisOption = thisKey.options.filter((o) => o.id === value)[0];
    if (thisOption?.submenus !== undefined) {
      for (let is = 0; is < thisOption.submenus.length; is++) {
        const sub = thisOption.submenus[is];
        const subKey = descriptionKeys.filter((k) => k.id === sub)[0];
        const nexUp = updateOneFieldOfDataFromKeys(
          descriptionKeys,
          updatedData,
          subKey.name,
          subKey.default
        );
        updatedData = { ...nexUp.updatedData };
        keysChanged = [...nexUp.keysChanged, key];
      }
    }
  }
  return { updatedData, keysChanged };
}

export const selectUpdateOneFieldOfData = (state) => (oldData, key, value) =>
  updateOneFieldOfDataFromKeys(state.annotations.description.keys, oldData, key, value);

export const selectAnnotationsStyles = (state) => {
  return state.annotations.annotationStyles
}

export const selectAnnotationStyleLabel = (state) => {
  return state.annotations.annotationStyles.label
}

export const selectAnnotationStyles = (state) => {
  return state.annotations.styles
}


// Initial filters
export const getDefaultFilters = createSelector([allDescriptionKeys], (allKeys) => {
  let output = {};
  for (const k of allKeys){
    if (["select"].indexOf(k.type) === -1) continue;
    // Use a unique key for submenus
    // rule is to use the id if it exists - id existing means that the key is a submenu
    // if not fallback to the name because the key is a parent - parents keys can't have the same name
    const filterKey = k.id ?? k.name;
    if (output[filterKey] == null) output[filterKey] = {};
    k.options.forEach((o) => {
      output[filterKey][o.text] = true;
    });
  }
  return output;
});

// Filters
export const selectFilters = createSelector( [getDefaultFilters, selectFilterSelections], (default_filters, filter_selections) => {
  let output = {...default_filters};
  for (const filter_key of Object.keys(filter_selections)){
    if (output[filter_key] == null){
      output[filter_key] = filter_selections[filter_key];
    }
    else {
      output[filter_key] = {
        ...output[filter_key],
        ...filter_selections[filter_key]
      };
    }
  }
  return output;
});

// This func returns the parent option linked to the given submenu key
// return: { parentKey, parentOptionId }
// parentKey: the parent key
// parentOptionId: the id of the parent option
// used to filter annotations by submenu
const findParentForSubmenu = (submenuId, allKeys) => {
  for (const parentKey of allKeys) {
    if (parentKey.type !== "select") continue;
    for (const opt of parentKey.options || []) {
      if ((opt.submenus || []).includes(submenuId)) {
        return { parentKey, parentOptionId: opt.id };
      }
    }
  }
  return null;
};

const findKeyForFilter = (filterKey, allKeys) =>
  allKeys.find((k) => k.id === filterKey) ?? allKeys.find((k) => k.name === filterKey);

// Filtered annotations (filter by photo used for map annotations; in ortho: elements are already ortho-only)
export const selectFilteredAnnotations = createSelector( [allDescriptionKeys, selectFilters, selectAnnotations, selectDisplayedPhotoForAnnotations], (allKeys, filters, all_annotations, displayedPhotoForAnnotations) => {
  let output = [...all_annotations];
  if (displayedPhotoForAnnotations?.id != null) {
    output = output.filter((a) => String(a.photoId) === String(displayedPhotoForAnnotations.id));
  }
  // Get the display values for each annotation
  // structure: { annotationId: { keyName: value } }
  let values_to_display_by_id = {};

  // Ensure the display values are correctly formatted
  for (const ann of output){
    values_to_display_by_id[ann.id] = cleanCorruptedAnnotation(allKeys, ann, "display").data;
  }
  // Applying the filters
  // for each filter, true = keep the annotation, false = hide it.
  Object.entries(filters).forEach(([filterKey, selector]) => {
    if (filterKey === "createdById") {
      // 2025-02-13, Pas fonctionnel, trop risqué tant qu'on est pas assuré côté backend de remplir ce champ
      /*output = output.filter((a) => {
        const wantedEntries = Object.entries(selector)
          .filter(([id, isWanted]) => isWanted)
          .map(([id, isWanted]) => id);
        return wantedEntries.indexOf(a.createdById) > -1;
      });*/
    } else {
      const k = findKeyForFilter(filterKey, allKeys);
      // Filters only apply to select keys (see filterableTypes). number/checkbox were disabled 2025-02-13.
      if (!k || k.type !== "select") return;

      // selector is an object with the following structure:
      // {
      //   "option1": true,
      //   "option2": false,
      //   "option3": true,
      // }
      const wantedEntries = [];
      const unwantedEntries = [];
      for (const [text, isWanted] of Object.entries(selector)) {
        if (isWanted) wantedEntries.push(text);
        else unwantedEntries.push(text);
      }

      // If k is a submenu, find the parent option
      // parent is necessary to cause data in annotation isn't enough to identify 
      // the right submenu cause name is not unique
      const parentLink = k.id ? findParentForSubmenu(k.id, allKeys) : null;

      output = output.filter((a) => {
        if (parentLink && a.data[parentLink.parentKey.name] !== parentLink.parentOptionId) {
          return true; // keep: this submenu filter does not apply to this annotation
        }
        const value_to_display = values_to_display_by_id[a.id][k.name];
        if (wantedEntries.includes(value_to_display)) return true; // keep: option checked
        if (unwantedEntries.includes(value_to_display)) return false; // hide: option unchecked
        return true; // keep: display value not in the selector
      });
    }
  });
  return output;
});


export const createAnnotationOnAPI = async ({ viewId, inspectionId, geometryName, geometry, data, photoId, text }) => {
  const result = await apiClient.req(
    REQUESTS.create({
      viewId: viewId,
      inspectionId: inspectionId,
      geometryName: geometryName,
      geometry: geometry,
      data: data,
      photoId: photoId,
      text: text,
    })
  );
  if (result.data.data.createAnnotation == null) {
    throw new Error("An error occured: " + result);
  }
  return result.data.data.createAnnotation;
};


export const {
  updateAnnotation,
  updateAnnotations,
  addCandidateAnnotation,
  clearCandidateAnnotations,
  createAnnotation,
  replaceAnnotations,
  updateViewSpec,
  deleteAnnotations,
  updateAnnotationState,
  setPresets,
  setViewAnnotationStyles,
  updateAnnotationStyleLabel,
} = annotationsSlice.actions;